import React from "react";
import HeroIcon from "../common/HeroIcon";
import IdeaGenerationSettingsPopover from "./IdeaGenerationSettingsPopover";
import { BrandAvatar } from "./AdaptBrandSelector";

const PATTERN_TYPE_ICONS = {
  type: "document-text",
  main_topic: "tag",
  content_angle: "light-bulb",
  format: "squares-2x2",
  structure: "queue-list",
  categories: "folder",
  tone: "swatch",
  audience: "users",
  language: "language",
  hook_type: "bolt",
  hook_phrase: "chat-bubble-left-ellipsis",
  hook_visual: "eye",
  cta_type: "cursor-arrow-rays",
  cta_style: "megaphone",
  hashtags: "hashtag",
  channel_tags: "at-symbol",
  text_hook: "bars-3-bottom-left",
  song: "musical-note",
  artist: "user-circle",
};

const getPatternIcon = (patternType) =>
  PATTERN_TYPE_ICONS[patternType] || "squares-2x2";

/**
 * Big A + B = C empty state for ideas. Brand + Pattern = Expected result.
 */
const IdeasEmptyState = ({
  pattern,
  patternTypeLabel,
  patternType,
  brand,
  onGenerateIdeas,
  onSettingsToggle,
  isGenerating,
  promotionValue = 5,
  onPromotionChange,
  promptValue,
  onPromptChange,
  numIdeas = 3,
  onNumIdeasChange,
  settingsOpen,
}) => {
  const videoCount = pattern?.stats?.video_count;
  const composite = pattern?.creator_comparison?.composite_outlier;
  const patternName = pattern?.name ?? pattern?.id ?? "Pattern";
  const resolvedPatternType = patternType ?? pattern?.pattern_type ?? "";
  const typeLabel = patternTypeLabel ?? pattern?.pattern_type_label ?? (resolvedPatternType || "Pattern");
  const patternIcon = getPatternIcon(resolvedPatternType);
  const brandName = brand?.name ?? "your brand";

  const pct = composite != null && typeof composite === "number" && !Number.isNaN(composite)
    ? Math.round((composite - 1) * 100)
    : null;
  const isPositive = pct != null && pct >= 0;

  return (
    <div className="rounded-xl border border-theme-secondary bg-theme-secondary p-8">
      <div className="flex flex-col items-center gap-8">
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <div className="flex flex-col items-center gap-2 min-w-[100px]">
            <div className="w-16 h-16 rounded-full border-2 border-theme-secondary bg-theme-main flex items-center justify-center overflow-hidden">
              <BrandAvatar brand={brand} size="lg" />
            </div>
            <span className="text-sm font-medium text-theme-text">{brandName}</span>
          </div>
          <span className="text-2xl text-theme-text opacity-50 font-light">+</span>
          <div className="flex flex-col items-center gap-2 min-w-[100px]">
            <div className="w-16 h-16 rounded-full border-2 border-theme-secondary bg-theme-main flex items-center justify-center text-theme-primary">
              <HeroIcon name={patternIcon} className="w-8 h-8" />
            </div>
            <span className="text-sm font-medium text-theme-text text-center max-w-[120px]">
              {patternName}
            </span>
          </div>
          <span className="text-2xl text-theme-text opacity-50 font-light">=</span>
          <div className="flex flex-col items-center gap-2 min-w-[100px]">
            <div
              className={`w-20 h-20 rounded-full border-2 flex items-center justify-center text-2xl font-bold ${
                isPositive
                  ? "border-green-500/50 bg-green-500/10 text-green-600"
                  : "border-red-500/50 bg-red-500/10 text-red-600"
              }`}
            >
              {pct != null ? `${pct >= 0 ? "+" : ""}${pct}%` : "—"}
            </div>
            <span className="text-xs text-theme-text opacity-70">
              Estimated Creator Boost
            </span>
          </div>
        </div>

        <p className="text-base text-theme-text opacity-90">
          Use this {typeLabel} pattern for {brandName}
        </p>

        <div className="flex items-center gap-2">
          <IdeaGenerationSettingsPopover
            promotionValue={promotionValue}
            onPromotionChange={onPromotionChange}
            promptValue={promptValue}
            onPromptChange={onPromptChange}
            numIdeas={numIdeas}
            onNumIdeasChange={onNumIdeasChange}
            isOpen={settingsOpen}
            onToggle={onSettingsToggle}
            position="up"
          />
          <button
            type="button"
            onClick={onGenerateIdeas}
            disabled={isGenerating}
            className="px-8 py-3 rounded-lg bg-theme-primary text-theme-button text-base font-semibold hover:opacity-85 disabled:opacity-50"
          >
            {isGenerating ? "Generating..." : `Generate ${numIdeas} Script Ideas`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IdeasEmptyState;
